import { Box, Pagination, TableRow, TextField, useTheme } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAgenda } from '../../redux/actions/agenda.actions'

import { Copy } from '@phosphor-icons/react'
import ClipLoader from 'react-spinners/ClipLoader'
import { useGetUser } from '../../hooks/useGetUser'
import { Button } from '../../shared/button/button'
import { Loader } from '../../shared/loader/loader'
import {
  NormalText,
  RowStyled,
  TableBodyStyled,
  TableCellStyled,
  TableHeadStyled,
  TableStyled,
  TooltipStyled,
} from '../../shared/styles'
import { copyToClipboard, formatLargeText, normalizeText } from '../../shared/utils'
import { ModalCreateEditContact } from './modal-create-edit-contact'
import { ModalDeleteContact } from './modal-delete-contact'

// Cuantos contactos se muestran por pagina. La paginacion es del lado del
// cliente: la agenda se trae entera y se filtra/pagina en el navegador.
const PAGE_SIZE = 10

export const AgendaContainer = () => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showModals, setShowModals] = useState({
    createContact: false,
    editContact: false,
    deleteContact: false,
  })
  const [contactSelected, setContactSelected] = useState(null)

  const {
    data: agenda,
    isLoading: isLoadingAgenda,
    error,
  } = useSelector((state) => state.agenda)

  const { user } = useGetUser()
  const { palette } = useTheme()
  const dispatch = useDispatch()

  // Filtrado en cliente: los campos de la agenda estan cifrados en la base, asi
  // que no hay busqueda parcial ni case/acento-insensitive del lado del backend.
  // Se trae la agenda entera y se filtra aca por alias, nombre, apellido, email
  // y wallet, normalizando para ignorar acentos y mayusculas.
  const filteredAgenda = useMemo(() => {
    const needle = normalizeText(search.trim())
    if (!needle) return agenda
    return agenda.filter((c) =>
      normalizeText(
        `${c.alias ?? ''} ${c.nombre ?? ''} ${c.apellido ?? ''} ${c.email ?? ''} ${c.walletadress ?? ''}`
      ).includes(needle)
    )
  }, [agenda, search])

  const pageCount = Math.max(1, Math.ceil(filteredAgenda.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const visibleAgenda = useMemo(
    () =>
      filteredAgenda.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
      ),
    [filteredAgenda, currentPage]
  )

  function handleShowModal(modal) {
    setShowModals({ ...showModals, [modal]: !showModals[modal] })
  }

  function handleDeleteContact(contact) {
    setShowModals({ ...showModals, deleteContact: true })
    setContactSelected(contact)
  }

  function handleEditContact(contact) {
    setShowModals({ ...showModals, editContact: true })
    setContactSelected(contact)
  }
  useEffect(() => {
    if (user?.usuarioid) {
      dispatch(getAgenda(user.usuarioid, 1, 100000))
    }
  }, [dispatch, user?.usuarioid])

  // Al cambiar la busqueda se vuelve a la primera pagina.
  useEffect(() => {
    setPage(1)
  }, [search])
  if (error)
    return (
      <NormalText color={'#EA7A7A'} size={14}>
        Error loading agenda
      </NormalText>
    )

  return (
    <Box>
      <ModalCreateEditContact
        open={showModals?.createContact || showModals?.editContact}
        handleClose={handleShowModal}
        idContact={contactSelected?.idwallet}
        contactData={contactSelected}
        isEdit={showModals?.editContact}
        userId={user?.usuarioid}
      />
      <ModalDeleteContact
        open={showModals?.deleteContact}
        handleClose={handleShowModal}
        idContact={contactSelected?.idwallet}
        userId={user?.usuarioid}
      />
      <Box display="flex" alignItems="center" gap={'24px'}>
        <Button
          label={
            isLoadingAgenda ? (
              <Loader size={16} color="#0197f6" />
            ) : (
              'Create contact'
            )
          }
          endIcon={!isLoadingAgenda && 'PlusCircle'}
          onClick={() => handleShowModal('createContact')}
          buttonStyles={{ minWidth: '162px' }}
          containerStyles={{
            maxHeight: '39px',
          }}
        />
        <TextField
          label="Search by alias, name, last name or wallet"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 320 }}
        />
      </Box>
      <Box
        display={'flex'}
        flexWrap={'wrap'}
        gap={'8px'}
        mt={'24px'}
        justifyContent={'space-between'}
      >
        {isLoadingAgenda ? (
          <ClipLoader color={'#0197f6'} size={24} />
        ) : filteredAgenda.length > 0 ? (
          <>
            <TableStyled>
              <TableHeadStyled>
                <TableRow>
                  <TableCellStyled align="center">Alias</TableCellStyled>
                  <TableCellStyled align="center">Email</TableCellStyled>
                  <TableCellStyled align="center">Name</TableCellStyled>
                  <TableCellStyled align="center">Last name</TableCellStyled>
                  <TableCellStyled align="center">Address</TableCellStyled>
                  <TableCellStyled align="center">Receives</TableCellStyled>
                  <TableCellStyled align="center">Actions</TableCellStyled>
                </TableRow>
              </TableHeadStyled>
              <TableBodyStyled>
                {visibleAgenda.map((contact) => (
                  <RowStyled
                    key={contact.idwallet}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCellStyled align="center">
                      {contact.alias}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      {formatLargeText(contact.email || '-', 20)}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      {contact.nombre || '-'}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      {contact.apellido || '-'}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      <Box
                        display={'flex'}
                        gap={'8px'}
                        alignItems={'center'}
                        justifyContent={'center'}
                      >
                        <TooltipStyled
                          title={contact.walletadress}
                          placement="top"
                        >
                          <NormalText size={14} color={'#272A24'}>
                            {formatLargeText(contact.walletadress || '-', 45)}
                          </NormalText>
                        </TooltipStyled>
                        <TooltipStyled title="Copy" placement="top">
                          <Copy
                            color={'#8C8C8C'}
                            size={16}
                            onClick={() =>
                              copyToClipboard(contact.walletadress)
                            }
                            style={{
                              cursor: 'pointer',
                            }}
                          />
                        </TooltipStyled>
                      </Box>
                    </TableCellStyled>
                    {/*
                      Red y token declarados. "Not set" marca los contactos
                      viejos: mientras esten asi, el portal no fija nada al
                      pagarles y se puede volver a mandar el token equivocado.
                    */}
                    <TableCellStyled align="center">
                      <NormalText size={14} color={contact.token ? '#272A24' : '#8C8C8C'}>
                        {contact.token
                          ? `${contact.token} · ${contact.chain}`
                          : 'Not set'}
                      </NormalText>
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      <Box
                        display={'flex'}
                        gap={'8px'}
                        alignItems={'center'}
                        justifyContent={'center'}
                      >
                        <Button
                          type={'regular'}
                          iconColor={palette.primary.main}
                          startIcon={'PencilSimple'}
                          onClick={() => handleEditContact(contact)}
                          tooltipLabel={'Edit'}
                          buttonStyles={{
                            '&&:hover': {
                              svg: {
                                fill: `${palette.secondary.main} !important`,
                              },
                            },
                          }}
                        />
                        <Button
                          type={'regular'}
                          iconColor={palette.error.main}
                          startIcon={'Trash'}
                          onClick={() => handleDeleteContact(contact)}
                          tooltipLabel={'Delete'}
                          buttonStyles={{
                            '&&:hover': {
                              svg: {
                                fill: `${palette.secondary[2]} !important`,
                              },
                            },
                          }}
                        />
                      </Box>
                    </TableCellStyled>
                  </RowStyled>
                ))}
              </TableBodyStyled>
            </TableStyled>
            {pageCount > 1 && (
              <Box
                display={'flex'}
                justifyContent={'center'}
                mt={'8px'}
                width={'100%'}
              >
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(_, value) => setPage(value)}
                  size="large"
                  sx={{ alignSelf: 'center', marginTop: '8px' }}
                />
              </Box>
            )}
          </>
        ) : (
          <NormalText size={14}>No contacts found</NormalText>
        )}
      </Box>
    </Box>
  )
}
